/**
 * The rules of the variant, in one place. Both the in-app Rules panel and the README's
 * rules section are rendered from this array, so the two can't drift: the README block
 * between its `RULES:BEGIN`/`RULES:END` markers is generated by `rulesMarkdown()` and a
 * test fails if the file on disk no longer matches (see test/ui/rules-text.test.ts, which
 * also rewrites it when run with `UPDATE_README=1`).
 *
 * Inline markup is deliberately tiny — `**bold**`, `` `code` `` and `[text](url)` — because
 * both renderers have to understand it.
 */

import type { MapId, Variant } from '../engine/types.js';

export type RulesBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'bullets'; items: string[] }
  | { kind: 'table'; head: string[]; rows: string[][] };

export const RULES_TITLE = 'The rules of this variant';

export const VIDEO_URL = 'https://www.youtube.com/watch?v=1ffyKPaCW5g';

const CONWAY_INTRO: RulesBlock[] = [
  { kind: 'heading', text: 'The variant, briefly' },
  {
    kind: 'para',
    text:
      'Standard [Diplomacy](https://en.wikipedia.org/wiki/Diplomacy_(game)) on the standard map, ' +
      'plus a step of Conway\'s Game of Life applied to the board twice a year. The variant is ' +
      'Ezio\'s, presented on DiploStrats: ' +
      `[Conway's Game of Diplomacy](${VIDEO_URL}).`,
  },
  {
    kind: 'para',
    text:
      'A year runs: **Spring Movement → Spring Retreats** (if needed) **→ Life step → Summer Spawn ' +
      'Choices** (if needed) **→ Fall Movement → Fall Retreats** (if needed) **→ Winter Builds → ' +
      'Life step → Winter Spawn Choices** (if needed). The Life step runs whether or not anything ' +
      'was dislodged or built.',
  },
  {
    kind: 'para',
    text:
      'Resolution follows the standard rules of Diplomacy — nothing about movement, support, ' +
      'convoys, retreats or adjustments is changed. It is tested against 160 cases from the DATC, ' +
      'the standard adjudication test suite, ported from the python `diplomacy` package\'s ' +
      '`test_datc.py`.',
  },
];

const LIFE: RulesBlock[] = [
  { kind: 'heading', text: 'The Life step' },
  {
    kind: 'para',
    text:
      'A province\'s neighbours are the provinces it touches on the map — by land or by sea, and ' +
      'whether or not either unit type could actually move between them. Sea provinces count like ' +
      'any other, and a province with two coasts is one province. Every province updates ' +
      'simultaneously:',
  },
  {
    kind: 'table',
    head: ['Province', 'Occupied neighbours', 'Result'],
    rows: [
      ['occupied', '0–1', 'the unit dies (loneliness)'],
      ['occupied', '2–3', 'survives'],
      ['occupied', '4 or more', 'the unit dies (overcrowding)'],
      ['empty', 'exactly 3', 'a new unit is born'],
    ],
  },
  {
    kind: 'para',
    text:
      'A newborn unit belongs to whichever power supplied at least two of its three parents; with ' +
      'no such majority it is **neutral**.',
  },
  {
    kind: 'para',
    text:
      'Its type follows the province — fleet at sea, army inland — but on the coast it is the ' +
      'owning player\'s choice. Those choices are made in the **Spawn ' +
      'Choices** phase that follows a Life step, entered and reported as build orders: ' +
      '`England: Build F Edi`. If no choice is given, the new unit is an army.',
  },

  { kind: 'heading', text: 'Neutral units' },
  {
    kind: 'bullets',
    items: [
      'They never move and are never ordered.',
      'They may receive support to hold, and they block movement and occupy their province like any unit.',
      'If dislodged, a neutral unit disbands, because it cannot submit a retreat.',
      'They never capture supply centres, and a neutral sitting on a home centre blocks a build there.',
      'They count as neighbours and as parents: two neutral parents plus one power\'s own parent ' +
        'produce a neutral birth, not a birth for that power.',
    ],
  },
  {
    kind: 'para',
    text:
      'A neutral\'s **unit type is a drawing convention, not a rule** — nothing in play can depend on ' +
      'it. At sea a neutral is drawn as a fleet, inland as an army, and on the coast the tool ' +
      'draws it as an army rather than asking.',
  },
];

const CONWAY_EDGE_CASES: RulesBlock[] = [
  { kind: 'heading', text: 'Edge cases' },
  {
    kind: 'bullets',
    items: [
      '**Split-coast spawns are this tool\'s own ruling.** A fleet born on Spain, Bulgaria or ' +
        'St Petersburg also names its coast, chosen by the owning player like the unit type. The ' +
        'video never addresses which coast a split-coast spawn takes; everything else on this page ' +
        'is either stated in the video or standard Diplomacy.',
      'Life runs after Spring\'s retreats even if nothing was dislodged, and after Winter\'s ' +
        'adjustments even if nobody built. Winter\'s order is builds and removals first, then Life — ' +
        'so a fresh build can die in the same Life pass, and no spawn occurs in a province a build ' +
        'was just placed in, because that province is already occupied when Life runs.',
      'Retreats resolve before Life: a unit that successfully retreated counts at its new province ' +
        'when Life runs, and a disbanded unit no longer counts as present.',
      'Two provinces are neighbours whenever they touch on the map, whether by land or by sea, and ' +
        'whether or not the units in them could actually move between them: an army in Marseilles ' +
        'and a fleet on Spain\'s north coast are neighbours, though the fleet could never move to ' +
        'Marseilles. A province with two coasts is one province, and Switzerland is excluded, since ' +
        'it can never hold a unit.',
      'Captures happen at the standard time, between Fall retreats and Winter builds. A Summer-born ' +
        'unit sitting on a centre through Fall captures it ' +
        'that Fall like any unit; a Winter-born unit captures at the next Fall; neutrals never capture.',
      'After the Winter Life step a power\'s unit count may differ from its centre count. Nothing ' +
        'corrects that until the following Winter.',
      'Civil disorder: unordered units hold. If a power owes removals it didn\'t order, the tool ' +
        'auto-removes the units furthest from home by the standard distance-from-home rule rather ' +
        'than prompting again.',
      'Controlling 18 supply centres is generally a solo victory, although this adjudicator ' +
        'doesn\'t enforce any particular win condition.',
      'If one unit is given more than one order, the last one wins, and the tool flags the unit so ' +
        'the GM can see it happened.',
    ],
  },
];

const SHARING: RulesBlock[] = [
  { kind: 'heading', text: 'Sharing and saving' },
  {
    kind: 'bullets',
    items: [
      'A **share link** hands anyone an independent copy of the current board — the same app, and ' +
        'nothing done there affects your game. Spawn-choice states are real board states, so they ' +
        'share too.',
      'The **offline build** (`conway-diplomacy.html`, linked in the page footer) is one HTML file ' +
        'with everything inlined; download it and open it from your own disk, with no network at all.',
      '**Export / Import JSON** saves or restores a whole game, history included.',
    ],
  },
];

/** Conway's Game of Diplomacy on the standard map: the rules the README carries. */
export const RULES: RulesBlock[] = [...CONWAY_INTRO, ...LIFE, ...CONWAY_EDGE_CASES, ...SHARING];

const DATC =
  'Resolution follows the standard rules of Diplomacy, tested against 160 cases from the DATC, ' +
  'the standard adjudication test suite, ported from the python `diplomacy` package\'s ' +
  '`test_datc.py`.';

const PLAIN_YEAR =
  'A year runs: **Spring Movement → Spring Retreats** (if needed) **→ Fall Movement → Fall ' +
  'Retreats** (if needed) **→ Winter Builds**. Centres change hands after Fall retreats.';

const PLAIN_EDGE_CASES = (solo: number): RulesBlock => ({
  kind: 'bullets',
  items: [
    'Civil disorder: unordered units hold. If a power owes removals it didn\'t order, the tool ' +
      'auto-removes the units furthest from home by the standard distance-from-home rule.',
    `Controlling ${solo} supply centres is a solo victory, although this adjudicator doesn't ` +
      'enforce any particular win condition.',
    'If one unit is given more than one order, the last one wins, and the tool flags the unit so ' +
      'the GM can see it happened.',
  ],
});

/** Plain Diplomacy on the standard map: the Life step switched off. */
const STANDARD_PLAIN: RulesBlock[] = [
  { kind: 'heading', text: 'Plain Diplomacy' },
  {
    kind: 'para',
    text:
      'This game is standard [Diplomacy](https://en.wikipedia.org/wiki/Diplomacy_(game)) on the ' +
      'standard map, with no Life step: the tool is simply an adjudicator.',
  },
  { kind: 'para', text: PLAIN_YEAR },
  { kind: 'para', text: DATC },
  { kind: 'heading', text: 'Edge cases' },
  PLAIN_EDGE_CASES(18),
  ...SHARING,
];

const GOLARION_PAGE = 'https://apriiori.com/projects/golarion-diplomacy/';

/** Golarion Diplomacy, summarised from its published page. */
const GOLARION: RulesBlock[] = [
  { kind: 'heading', text: 'Golarion Diplomacy' },
  {
    kind: 'para',
    text:
      'Seven powers around the Inner Sea of Golarion, beginning in Spring 4701: Andoran, ' +
      'Cheliax, Nidal, Osirion, Qadira, Rahadoum and Taldor. There are **36 supply centres, ' +
      'and 19 is a solo.** The map, the full province list and the adjacency notes are on ' +
      `[the Golarion Diplomacy page](${GOLARION_PAGE}); this is a summary.`,
  },
  {
    kind: 'bullets',
    items: [
      'As in classic Diplomacy, a power builds in Winter in its own home centres, if the centre is ' +
        'unoccupied and still under its control.',
      '**Absalom is a neutral build centre:** any power that controls it may build an army or a ' +
        'fleet there, so long as it is unoccupied.',
      '**The Eye of Abendego is impassable.** No unit can enter it.',
      '**Corentyn and Ravounel have split coasts.** Cor/wc reaches Pezzack, MAO and Khari; Cor/ec ' +
        'reaches Westcrown, WES and Khari. Rav/wc reaches Pezzack, NAO, STM and Vyre; Rav/nc ' +
        'reaches Vyre, CON and Nisroch.',
      '**Khari holds the Hespereth Strait** and works like Constantinople or Kiel: fleets pass ' +
        'through it between MAO and WES, and armies cross it between Avistan and Garund.',
      'Armies reach **Absalom, Mediogalti Island, Okeno and Jalmeray** only by convoy.',
      'The unlabelled lakes and islands are drawn for flavour and are not part of the board.',
      'All other rules are as in classic Diplomacy.',
    ],
  },
];

const GOLARION_PLAIN: RulesBlock[] = [
  ...GOLARION,
  { kind: 'heading', text: 'Turns' },
  { kind: 'para', text: PLAIN_YEAR },
  { kind: 'para', text: DATC },
  { kind: 'heading', text: 'Edge cases' },
  PLAIN_EDGE_CASES(19),
  {
    kind: 'para',
    text:
      'This tool\'s reading of the Absalom rule: "under their control" means the power owns the ' +
      'centre in the usual way, so a power that takes Absalom in the Fall can build there that ' +
      'Winter. ' +
      'Absalom is nobody\'s home centre, so it does not count as home when civil disorder ' +
      'picks which units to remove.',
  },
  ...SHARING,
];

/**
 * Conway on Golarion works mechanically — the Life step runs over Golarion's own
 * neighbours — but nobody has balanced it, and the page says so.
 */
const GOLARION_CONWAY: RulesBlock[] = [
  ...GOLARION,
  { kind: 'heading', text: "With Conway's Life step" },
  {
    kind: 'para',
    text:
      'This game adds the Life step from Ezio\'s ' +
      `[Conway's Game of Diplomacy](${VIDEO_URL}) to the Golarion map. That pairing is this ` +
      'tool\'s own, and unbalanced: nobody has playtested it.',
  },
  {
    kind: 'para',
    text:
      'A year runs: **Spring Movement → Spring Retreats** (if needed) **→ Life step → Summer Spawn ' +
      'Choices** (if needed) **→ Fall Movement → Fall Retreats** (if needed) **→ Winter Builds → ' +
      'Life step → Winter Spawn Choices** (if needed).',
  },
  ...LIFE,
  { kind: 'heading', text: 'Edge cases' },
  {
    kind: 'bullets',
    items: [
      'Neighbours are the provinces that touch on the Golarion map, by land or by sea. Corentyn and ' +
        'Ravounel each count as one province, and the Eye of Abendego is left out, since it can ' +
        'never hold a unit. A fleet born on Corentyn or Ravounel names its coast.',
      'Captures happen between Fall retreats and Winter builds; neutrals never capture.',
      'Absalom is a build site for whichever power controls it; a unit sitting there blocks it.',
      'Controlling 19 supply centres is a solo victory, although this adjudicator doesn\'t enforce ' +
        'any particular win condition.',
    ],
  },
  ...SHARING,
];

/** The rules for the game in hand, by map and by whether the Life step is on. */
export function rulesFor(map: MapId, variant: Variant): RulesBlock[] {
  if (map === 'golarion') return variant === 'standard' ? GOLARION_PLAIN : GOLARION_CONWAY;
  return variant === 'standard' ? STANDARD_PLAIN : RULES;
}

/** One line on what the rules panel covers, for the ⋯ menu. */
/** The Rules panel's title. A plain game is not "this variant": it is Diplomacy. */
export function rulesTitle(map: MapId, variant: Variant): string {
  if (variant !== 'standard') return RULES_TITLE;
  return map === 'golarion' ? 'The rules of Golarion Diplomacy' : 'The rules of Diplomacy';
}

/** The page's own name for a game on this map and variant: the tab title, the footer. */
export function appName(map: MapId, variant: Variant): string {
  if (variant !== 'standard') return "Conway's Game of Diplomacy";
  return map === 'golarion' ? 'Golarion Diplomacy' : 'Diplomacy';
}

export function rulesHint(variant: Variant): string {
  return variant === 'standard'
    ? 'The turn order, the map\'s special rules and this tool\'s rulings.'
    : 'The Life step, neutrals, edge cases and this tool\'s own rulings.';
}

// ------------------------------------------------------------------ markdown

function mdBullet(text: string): string {
  return `- ${text}`;
}

/** The README's rules section, generated from `RULES`. */
export function rulesMarkdown(): string {
  const out: string[] = [];
  for (const block of RULES) {
    switch (block.kind) {
      case 'heading':
        out.push(`## ${block.text}`);
        break;
      case 'para':
        out.push(block.text);
        break;
      case 'bullets':
        out.push(block.items.map(mdBullet).join('\n'));
        break;
      case 'table':
        out.push(
          [
            `| ${block.head.join(' | ')} |`,
            `| ${block.head.map(() => '---').join(' | ')} |`,
            ...block.rows.map((r) => `| ${r.join(' | ')} |`),
          ].join('\n'),
        );
        break;
    }
  }
  return out.join('\n\n');
}
